import {ShowMore, type ShowMoreRef, type ShowMoreToggleLinesFn} from '@re-dev/react-truncate'
import * as React from 'react'

import LocationSvg from '@/assets/food/icons/location.svg?react'
import StartSvg from '@/assets/icons/star/full.svg?react'
import type {Result} from '@/lib/food-grubhub/foodGrubhubModel'
import {FoodGrubhubDetailRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './StoreCardGrubhub.module.scss'

interface StoreCardProps {
  store: Result
  headingLevel?: number
  className?: string
}

export default memo(function StoreCardGrubhub(props: StoreCardProps) {
  const {store} = props

  // const log_image_format = `${store.additional_media_images.LOGO_HOME_PAGE?.base_url}${store.additional_media_images.LOGO_HOME_PAGE?.public_id}.${store.additional_media_images.LOGO_HOME_PAGE?.format}`
  const descriptionElement = useRef<HTMLDivElement>(null)
  const ref = useRef<ShowMoreRef>(null)
  const formattedRating = store.ratings.actual_rating_value?.toFixed(1)
  const storeAddressFormat = `${store.address.street_address}, ${store.address.address_locality}, ${store.address.postal_code}`

  const toggleLines: ShowMoreToggleLinesFn = e => {
    ref.current?.toggleLines(e)
  }

  return (
    <div className={style.Store}>
      <Link
        to={FoodGrubhubDetailRoute.fullPath}
        params={{
          restaurantId: {
            restaurant_id: store.restaurant_id ?? '',
          },
        }}
        className={style.StoreLink}
      >
        <img src={store.logo ?? ''} alt='' className={style.StoreImage} loading='lazy' />
      </Link>
      <div className={style.StoreContent}>
        <div className={style.StoreName}>{store.name}</div>
        <div className={style.StoreInfo}>
          <div className={style.StoreInfoDesc}>
            <VisuallyHidden>
              Store description: {store.description?.length === 0 && 'Not available'}
            </VisuallyHidden>
            <div ref={descriptionElement}>
              <ShowMore
                ref={ref}
                lines={3}
                more={
                  <button onClick={toggleLines} className={style.ReadMore} type='button'>
                    Read more
                  </button>
                }
                less={
                  <button onClick={toggleLines} className={style.ReadMore} type='button'>
                    Read less
                  </button>
                }
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: store.description ?? '',
                  }}
                />
              </ShowMore>
            </div>
          </div>
          <div className={style.StoreInfoDetail}>
            <div className={style.StoreDetailItem}>
              <div className={style.StoreDetailItemValue}>
                <VisuallyHidden>Delivery time: </VisuallyHidden>
                {`${(store.delivery_time_estimate_lower_bound ?? 0).toString()} mins`}
              </div>
            </div>
            <div className={style.StoreDetailItemBreak}></div>
            <div className={style.StoreDetailItem}>
              <div className={style.StoreDetailItemValue}>
                <VisuallyHidden>Delivery distance: </VisuallyHidden>
                {`< ${store.distance_from_location} mi`}
              </div>
            </div>
            {typeof store.ratings.actual_rating_value === 'number' &&
              store.ratings.actual_rating_value > 0 && (
                <>
                  <div className={style.StoreDetailItemBreak}></div>
                  <div className={style.StoreDetailItem}>
                    <VisuallyHidden>
                      {`Rating: ${formattedRating ?? ''} out of 5 stars`}
                    </VisuallyHidden>
                    <p aria-hidden='true' className={style.StoreDetailItemValue}>
                      {formattedRating ?? ''}
                    </p>
                    <StartSvg className={style.StoreDetailItemIcon} />
                  </div>
                </>
              )}
          </div>
          <div className={style.StoreInfoStatus}>
            <div
              className={clsx(style.StoreInfoStatusIcon, store.open ? style.Opened : style.Closed)}
            ></div>
            <div className={style.StoreInfoStatusTitle}>
              <VisuallyHidden>Store status: </VisuallyHidden>
              {store.open ? 'Opening' : 'Closed'}
            </div>
          </div>
          <div className={style.StoreBreak}></div>
        </div>
        <div className={style.StoreAddress}>
          <LocationSvg className={style.StoreAddressIcon} />
          <div className={style.StoreAddressValue}>
            <VisuallyHidden>Store address: </VisuallyHidden>
            {storeAddressFormat}
          </div>
        </div>
      </div>
    </div>
  )
})
